/**
 * The card on file (mission 9 WP13 T082; sheet 50 callout 9; B.1, T.6; FR-007; S22; spec amendments 9
 * and 11; contracts/ui-api.md "Sheet 50"; research §1.17).
 *
 * `id="card"`: `/app/settings/billing#card` scrolls here (U28), and every "Update card" remedy links
 * here. States:
 * - `card`: `cardValue`, the brand and last four only, in mono; no other card data reaches Trellis;
 * - `none`: `cardNone`;
 * - `provider-unavailable`: `providerUnavailable`, when the read says the provider did not answer, or
 *   when the returned update URL is refused.
 *
 * Update card starts the provider's hosted update (`useCardUpdateMutation`), which leaves through
 * `leaveForProvider`: `safeProviderUrl` first, so the capability URL never reaches state or the DOM
 * (S22). It stays enabled while read-only: it is the fix (research §1.17). `cardProviderNote` states
 * where card details are entered (pending: copy owner, Compliance; S18).
 */
import { Band } from '@/components/trellis/Band'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { BillingView } from '@/lib/api/billing'
import { Mono } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { useCardUpdateMutation } from '@/lib/queries'

import { BillingProblem } from './BillingProblem'

export function CardOnFile({ view }: { view: BillingView }) {
  const update = useCardUpdateMutation()
  const { card } = view
  return (
    <Card id="card" className="scroll-mt-4">
      <CardHeader>
        <CardTitle>{copyText('cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {view.provider_unavailable ? (
          <Band kind="info">
            <p>{copyText('providerUnavailable')}</p>
          </Band>
        ) : (
          <p className="text-body text-navy-950">
            {card === null ? copyText('cardNone') : <Mono>{PROVISIONAL_COPY.cardValue.render(card)}</Mono>}
          </p>
        )}
        {update.error === null ? null : <BillingProblem error={update.error} />}
        <div className="flex items-center gap-4">
          <Button variant="secondary" loading={update.isPending} onClick={() => update.mutate()}>
            {copyText('cardUpdate')}
          </Button>
        </div>
        <p className="text-caption text-navy-600">{copyText('cardProviderNote')}</p>
      </CardContent>
    </Card>
  )
}
